import React, { useState } from 'react';
import axios from 'axios';
import { Plus, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Diagnostic test chips sourced from Services & Packages + inline "+ Add test".
export const IntakeTestChips = ({ chips, selected, onToggle, chipPrice, onAdded, canAdd }) => {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const reset = () => { setAdding(false); setName(''); setPrice(''); setErr(''); };
  const confirm = async () => {
    if (!name.trim() || price === '' || Number(price) < 0) { setErr('Enter a name and price'); return; }
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${API}/appointments/quick-test-service`, { name: name.trim(), price: Number(price) });
      onAdded(r.data);
      reset();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not save test');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap gap-1 items-center" data-testid="bk-recommended-tests">
      {chips.length === 0 && !adding && (
        <span className="text-[10px] text-slate-400 italic mr-1" data-testid="bk-rec-empty">No tests in Services &amp; Packages yet.</span>
      )}
      {chips.map((t) => {
        const on = selected.includes(t.key);
        const p = chipPrice(t.key);
        return (
          <button key={t.key} type="button" onClick={() => onToggle(t.key)}
            data-testid={`bk-rec-${t.testid}`}
            title={p != null ? `${t.label} · ₹${p.toLocaleString('en-IN')}` : t.label}
            className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border transition-colors ${
              on ? 'bg-sky-600 text-white border-sky-700' : 'bg-white text-slate-600 border-slate-300 hover:border-sky-400 hover:bg-sky-50'
            }`}>
            {on ? '✓ ' : ''}{t.label}
            {p != null && (
              <span className={`ml-1 tabular-nums ${on ? 'text-sky-100' : 'text-slate-400'}`}>· ₹{p.toLocaleString('en-IN')}</span>
            )}
          </button>
        );
      })}
      {!canAdd ? null : !adding ? (
        <button type="button" onClick={() => setAdding(true)} data-testid="bk-rec-add-btn"
          className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-semibold rounded-full border border-dashed border-sky-400 text-sky-700 hover:bg-sky-50 transition-colors">
          <Plus size={10} /> Add test
        </button>
      ) : (
        <div className="inline-flex items-center gap-1 px-1.5 py-1 rounded-lg border border-sky-300 bg-sky-50/60" data-testid="bk-rec-add-form">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Test name" maxLength={80}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } if (e.key === 'Escape') reset(); }}
            data-testid="bk-rec-add-name" className="w-32 px-1.5 py-0.5 text-[11px] border border-slate-300 rounded bg-white focus:outline-none focus:border-sky-500" />
          <span className="text-[11px] text-slate-500">₹</span>
          <input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Price"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirm(); } if (e.key === 'Escape') reset(); }}
            data-testid="bk-rec-add-price" className="w-20 px-1.5 py-0.5 text-[11px] border border-slate-300 rounded bg-white focus:outline-none focus:border-sky-500 tabular-nums" />
          <button type="button" onClick={confirm} disabled={busy} data-testid="bk-rec-add-confirm" title="Add test"
            className="p-1 rounded bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50"><Check size={11} /></button>
          <button type="button" onClick={reset} data-testid="bk-rec-add-cancel" title="Cancel"
            className="p-1 rounded text-slate-500 hover:bg-slate-200"><X size={11} /></button>
          {err && <span className="text-[10px] text-rose-600" data-testid="bk-rec-add-error">{err}</span>}
        </div>
      )}
    </div>
  );
};
